import React, { useState, useEffect, useRef } from "react";
import styles from "./Sidebar.module.css";
import { ReactComponent as LanguageIcon } from "../../assets/svg/language.svg";
import { ReactComponent as GamesIcon } from "../../assets/svg/games.svg";
import { ReactComponent as AppearanceIcon } from "../../assets/svg/appearance.svg";
import { translate, LANGUAGE_OPTIONS, t, noAutoTranslateUserTextProps } from "../../utils/i18n";
import { GameeBrand } from "../../utils/brand";
import { THEME_STORAGE_KEY } from "../../utils/themeStorage";
import { post } from "../../utils/request";
import { useNavigate, useLocation } from "react-router-dom";
import {
  gameHomePath,
  collectionsListPath,
  topicsListPath,
  isCollectionsSectionPath,
  isTopicsSectionPath,
  isGameHomePath,
  switchLangPath,
  languageIdToCode,
  isLangCategoryRouting,
} from "../../utils/routes";

const MOBILE_BREAKPOINT = 499;

const Sidebar = ({
  isOpen,
  onClose,
  theme,
  setTheme,
  isLoggedIn,
  handleOpenLogin,
  activeCategory, // 新增：受控分类ID
  onCategoryChange, // 新增：分类改变的回调
  categories: externalCategories, // 新增：外部传入的分类数据
  // 如果外部接管了分类获取，可以不传这部分让 Sidebar 自行请求，
  // 但为了性能和共享状态，建议外部传入。
}) => {
  // 内部状态管理
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isGamesExpanded, setIsGamesExpanded] = useState(true);
  const [internalActive, setInternalActive] = useState("");
  const [internalCategories, setInternalCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // ref 用于直接操作抽屉 DOM，避免用 state 带来的渲染时序问题
  const sidebarRef = useRef(null);

  // 点击菜单项时调用：立即移除 DOM 上的 transition，再关闭抽屉
  const closeInstantly = () => {
    if (sidebarRef.current) {
      sidebarRef.current.style.transition = "none";
    }
    onClose();
  };

  // 受控处理逻辑
  const categories = externalCategories || internalCategories;
  const sidebarActive =
    activeCategory !== undefined ? activeCategory : internalActive;
  const setSidebarActive = onCategoryChange || setInternalActive;
  const categoriesFetchedRef = useRef(false);
  const currentLang =
    typeof translate.currentLanguage === "function"
      ? translate.currentLanguage()
      : "";
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= MOBILE_BREAKPOINT,
  );
  // 折叠态：宽度在 500~1248px 之间，CSS 会隐藏文字标签，侧边栏仅显示图标
  const [isCollapsed, setIsCollapsed] = useState(
    window.innerWidth > MOBILE_BREAKPOINT && window.innerWidth <= 1248,
  );

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
      setIsCollapsed(
        window.innerWidth > MOBILE_BREAKPOINT && window.innerWidth <= 1248,
      );
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const isCollectionsPage = isCollectionsSectionPath(location.pathname);
  const isTopicsPage = isTopicsSectionPath(location.pathname);

  /** 首页主内容区（带分类筛选），此时由父组件 onCategoryChange 更新即可，无需整页跳转 */
  const isHomeMainFeed =
    typeof onCategoryChange === "function" &&
    !isCollectionsPage &&
    !isTopicsPage &&
    isGameHomePath(location.pathname);

  const handleGoHome = () => {
    if (typeof onCategoryChange === "function") {
      onCategoryChange("");
    }
    navigate(gameHomePath());
    if (isMobile) closeInstantly();
  };

  // 关闭移动端菜单(当切换到桌面端时)
  useEffect(() => {
    if (!isMobile && isOpen) {
      onClose();
    }
  }, [isMobile, isOpen, onClose]);

  // 抽屉重新打开时，用双 rAF 等元素渲染到新位置后再恢复 transition
  useEffect(() => {
    if (isOpen && sidebarRef.current) {
      // 第一个 rAF：等待浏览器完成当前帧渲染
      const raf1 = requestAnimationFrame(() => {
        // 第二个 rAF：元素已在不带 transition 的状态下渲染到起始位置，现在安全恢复
        const raf2 = requestAnimationFrame(() => {
          if (sidebarRef.current) {
            sidebarRef.current.style.transition = "";
          }
        });
        return () => cancelAnimationFrame(raf2);
      });
      return () => cancelAnimationFrame(raf1);
    }
  }, [isOpen]);

  // 展开 Games 时如果未提供外部数据才去请求
  useEffect(() => {
    if (externalCategories) return; // 如果外部传了数据，就不在这里请求
    if (categoriesFetchedRef.current) return;
    categoriesFetchedRef.current = true;
    setCategoriesLoading(true);
    post("/game/category/category-info", {})
      .then(res => {
        const list = res?.data ?? [];
        setInternalCategories(Array.isArray(list) ? list : []);
      })
      .catch(err => {
        console.warn("Get categories failed:", err);
      })
      .finally(() => setCategoriesLoading(false));
  }, [externalCategories]);

  return (
    <>
      {/* 侧边栏 */}
      <aside
        ref={sidebarRef}
        className={`${styles.sidebar} ${isOpen ? styles.mobileOpen : ""}`}
      >
        {/* Header: Hamburger + Logo */}
        <div className={styles.sidebarHeader}>
          {isMobile && (
            <button
              className={styles.sidebarCloseBtn}
              onClick={onClose}
              aria-label={t("aria.closeMenu", "Close menu")}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          {/* 点击回到首页 */}
          <div
            className={styles.title}
            role="button"
            tabIndex={0}
            onClick={handleGoHome}
            onKeyDown={e => e.key === "Enter" && handleGoHome()}
          >
            <GameeBrand /> Store
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={styles.sidebarContent}>
          <nav className={styles.sidebarNav}>
            {/* 游戏标题项 - 可展开/收起下方选项 */}
            <button
              type="button"
              className={styles.sidebarSectionItemActive}
              aria-label={t("sidebar.games", "Games")}
              onClick={() => {
                if (isCollapsed) {
                  // 折叠态：点击游戏 icon 直接跳首页
                  navigate(gameHomePath());
                } else {
                  // 展开态：展开/收起子菜单
                  setIsGamesExpanded(v => !v);
                }
              }}
            >
              <span className={styles.sidebarIconWrapper}>
                <GamesIcon />
              </span>
              <span className={styles.sidebarSectionLabel}>
                {t("sidebar.games", "Games")}
              </span>
            </button>

            {/* 二级菜单 - 来自 /game/category/category-info 接口，展开时展示 */}
            {isGamesExpanded && (
              <div className={styles.sidebarSubMenu}>
                {categoriesLoading ? (
                  <div className={styles.subMenuLoading}>
                    {t("common.loading", "Loading...")}
                  </div>
                ) : (
                  <>
                    {categories.slice(0, 8).map(item => {
                      const id = item.categoryId ?? item.categoryName ?? "";
                      return (
                        <button
                          type="button"
                          key={id}
                          className={styles.sidebarSubItemButton}
                          onClick={() => {
                            if (isHomeMainFeed) {
                              setSidebarActive(id);
                            } else {
                              navigate(gameHomePath({ category: id }));
                              if (!onCategoryChange) setSidebarActive(id);
                            }
                            if (isMobile) closeInstantly();
                          }}
                        >
                          {item.categoryName ?? ""}
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            )}

            {/* 合集 */}
            <button
              type="button"
              className={`${styles.sidebarSectionItemActive} ${isCollectionsPage ? styles.sidebarSectionItemHighlight : ""}`}
              aria-label={t("sidebar.collections", "Collections")}
              onClick={() => navigate(collectionsListPath())}
            >
              <span className={styles.sidebarIconWrapper}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="7" height="7" rx="1" />
                  <rect x="14" y="4" width="7" height="7" rx="1" />
                  <rect x="3" y="13" width="7" height="7" rx="1" />
                  <rect x="14" y="13" width="7" height="7" rx="1" />
                </svg>
              </span>
              <span className={styles.sidebarSectionLabel}>
                {t("sidebar.collections", "Collections")}
              </span>
            </button>

            {/* 话题 */}
            <button
              type="button"
              className={`${styles.sidebarSectionItemActive} ${isTopicsPage ? styles.sidebarSectionItemHighlight : ""}`}
              aria-label={t("sidebar.topics", "Topics")}
              onClick={() => navigate(topicsListPath())}
            >
              <span className={styles.sidebarIconWrapper}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 11.5a8.38 8.38 0 0 1-9 8.5 8.5 8.5 0 1 1 9-8.5Z" />
                  <polyline points="9 11 12 8 15 11" />
                  <line x1="12" y1="8" x2="12" y2="16" />
                </svg>
              </span>
              <span className={styles.sidebarSectionLabel}>
                {t("sidebar.topics", "Topics")}
              </span>
            </button>

            {/* Language & Appearance (moved into nav area) */}
            <div className={styles.sidebarFooter}>
              <div style={{ position: "relative", width: "100%" }}>
                <button
                  type="button"
                  className={`${styles.sidebarItem} ${styles.languageIconBox}`}
                  onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                  aria-label={t("sidebar.language", "Language")}
                >
                  <div className={styles.languageIconWrapper}>
                    <LanguageIcon />
                  </div>
                  <span>{t("sidebar.language", "Language")}</span>
                </button>
                {isLanguageMenuOpen && (
                  <>
                    <div
                      className={styles.themeMenuOverlay}
                      onClick={() => setIsLanguageMenuOpen(false)}
                    />
                    <div className={styles.themeMenu}>
                      {LANGUAGE_OPTIONS.map(opt => (
                        <button
                          key={opt.id}
                          type="button"
                          className={`${styles.themeOption} ignore notranslate`}
                          {...noAutoTranslateUserTextProps}
                          onClick={() => {
                            try {
                              window.localStorage.setItem("language", opt.id);
                            } catch (e) {
                              // ignore storage errors
                            }
                            setIsLanguageMenuOpen(false);
                            if (isLangCategoryRouting()) {
                              const nextPath = switchLangPath(
                                location.pathname,
                                languageIdToCode(opt.id),
                              );
                              if (nextPath) {
                                navigate(nextPath);
                                if (typeof translate.changeLanguage === "function") {
                                  translate.changeLanguage(opt.id);
                                }
                                return;
                              }
                            }
                            window.location.reload();
                          }}
                        >
                          {opt.name}
                          {currentLang === opt.id && (
                            <span className={styles.checkmark}>✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div style={{ position: "relative", width: "100%" }}>
                <button
                  type="button"
                  className={styles.sidebarItem}
                  onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
                  aria-label={t("sidebar.appearance", "Appearance")}
                >
                  <AppearanceIcon />
                  <span>{t("sidebar.appearance", "Appearance")}</span>
                </button>
                {isThemeMenuOpen && (
                  <>
                    <div
                      className={styles.themeMenuOverlay}
                      onClick={() => setIsThemeMenuOpen(false)}
                    />
                    <div className={styles.themeMenu}>
                      <button
                        className={styles.themeOption}
                        onClick={() => {
                          try {
                            localStorage.setItem(THEME_STORAGE_KEY, "dark");
                          } catch (e) {
                            // ignore
                          }
                          setTheme("dark");
                          setIsThemeMenuOpen(false);
                        }}
                      >
                        {t("sidebar.theme.dark", "Dark Theme")}
                        {theme === "dark" && (
                          <span className={styles.checkmark}>✓</span>
                        )}
                      </button>
                      <button
                        className={styles.themeOption}
                        onClick={() => {
                          try {
                            localStorage.setItem(THEME_STORAGE_KEY, "light");
                          } catch (e) {
                            // ignore
                          }
                          setTheme("light");
                          setIsThemeMenuOpen(false);
                        }}
                      >
                        {t("sidebar.theme.light", "Light Theme")}
                        {theme === "light" && (
                          <span className={styles.checkmark}>✓</span>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </nav>

          {/* Mobile Login Button */}
          {isMobile && !isLoggedIn && (
            <div className={styles.mobileLoginBtnContainer}>
              <button
                className={styles.mobileLoginBtn}
                onClick={handleOpenLogin}
              >
                {t("sidebar.loginCta", "Join for free or log in")}
              </button>
            </div>
          )}
        </div>

        {/* Sticky Footer */}
        <div className={styles.sidebarExtendedFooter}>
          <div className={styles.footerLinks}>
            <p>{t("sidebar.footer.links", "Developer · Legal · Terms · Privacy")}</p>
            <p>© 2026 <GameeBrand /></p>
          </div>
          <div className={styles.hopInTitle}>{t("sidebar.footer.slogan", "Hop in.")}</div>
          <div className={styles.socialIcons}>
            <div className={styles.socialIcon} aria-label={t("aria.facebook", "Facebook")}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
              </svg>
            </div>
            <div className={styles.socialIcon} aria-label={t("aria.discord", "Discord")}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-1.438-9.18-4.1-13.822a.076.076 0 0 0-.032-.027zM8.52 14.975c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.418 2.157-2.418 1.21 0 2.176 1.085 2.157 2.419 0 1.334-.956 2.419-2.157 2.419zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.175 1.085 2.157 2.419 0 1.334-.946 2.419-2.157 2.419z"></path>
              </svg>
            </div>
            <div className={styles.socialIcon} aria-label={t("aria.twitter", "Twitter")}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
              </svg>
            </div>
            <div className={styles.socialIcon} aria-label={t("aria.youtube", "Youtube")}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.33 29 29 0 0 0-.46-5.33zM9.75 15.02l5.75-3.27-5.75-3.27v6.54z"></path>
              </svg>
            </div>
            <div className={styles.socialIcon} aria-label={t("aria.reddit", "Reddit")}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.067 1.597a8.707 8.707 0 0 1 10 13.914 8.72 8.72 0 0 1-1.025 3.999 1.493 1.493 0 0 1 1.025 1.332c0 .968-.786 1.754-1.754 1.754-.42 0-.796-.145-1.096-.386-1.189.967-2.9 1.621-4.805 1.674l.169-3.953 2.716 5.751a1.25 1.25 0 0 1-1.25-1.25 1.25 1.25 0 0 1 1.25-1.25c.607 0 1.111.439 1.222 1.018l2.909-6.155a.188.188 0 0 1 .253-.095l.006.002.001.001c.092.043.128.156.085.247l-.195 1.096c-.453.228-.906.265-1.343.265-2.022 0-3.664-1.642-3.664-3.664 0-.156.012-.312.035-.468l-3.322-.705a.188.188 0 0 1-.148-.224l1.107-5.187z"></path>
              </svg>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay for closing drawer */}
      {isOpen && <div className={styles.drawerOverlay} onClick={onClose} />}
    </>
  );
};

export default Sidebar;
